import {useLingui} from '@lingui/react';
import {MerchantAccountPublicStatus, PayoutsFilters} from '@zentact/common';
import {MerchantAccountStatus} from '@zentact/db';
import {DateTime} from 'luxon';
import {useCallback, useEffect} from 'react';
import {MerchantAccountsPickerFilter} from '../dropdowns';
import {ResetTableFiltersButton} from '../table';
import {DashboardDatepicker} from './DashboardDatepicker';
import {EntityPickerFilter} from './EntityPickerFilter';

type Props<T extends PayoutsFilters> = {
  typedSearchParams: T;
  setTypedSearchParams: (params: T) => void;
  selectedOrganization?: string | null;
  selectedMerchantAccount?: string | null;
  organizationList?: {id: string; name: string}[];
  merchantAccounts?: {
    id: string;
    businessName: string;
    organizationId: string | null;
    status: MerchantAccountStatus | MerchantAccountPublicStatus;
    onboardedAt: string | null;
  }[];
  storeList?: {
    id: string;
    name: string;
    status: string;
  }[];
  storeId?: string;
  dateValue: {
    startDate: Date;
    endDate: Date;
  } | null;
  handleDateChange: (dateRange: {
    startDate: Date;
    endDate: Date;
  }) => void;
};

export const PayoutFilters = <T extends PayoutsFilters>({
  typedSearchParams,
  setTypedSearchParams,
  selectedOrganization,
  selectedMerchantAccount,
  storeList,
  storeId,
  merchantAccounts,
  dateValue,
  organizationList,
  handleDateChange,
}: Props<T>) => {
  const {i18n} = useLingui();

  const handleFilterChange = useCallback(
    <K extends keyof T>(key: K, value: T[K]) => {
      setTypedSearchParams({[key]: value} as unknown as T);
    },
    [setTypedSearchParams]
  );

  useEffect(() => {
    const isOrganizationMerchant = merchantAccounts
      ?.filter(merchant => merchant.organizationId === selectedOrganization)
      .some(merchant => merchant.id === selectedMerchantAccount);
    if (selectedOrganization && !isOrganizationMerchant) {
      setTypedSearchParams({merchantAccountId: undefined} as unknown as T);
    }
  }, [
    setTypedSearchParams,
    typedSearchParams.selectedOrganization,
    merchantAccounts,
    typedSearchParams.selectedMerchantAccount,
  ]);

  const handleFilterChangeWithStoreClear = useCallback(
    <K extends keyof T>(key: K, value: T[K]) => {
      setTypedSearchParams({
        pageIndex: undefined,
        pageSize: undefined,
        storeId: undefined,
        [key]: value,
      } as unknown as T);
    },
    [setTypedSearchParams]
  );

  return (
    <div className="flex flex-wrap items-center justify-end gap-2 lg:flex-nowrap">
      <div className="block lg:hidden 2xl:block max-lg:w-full">
        <ResetTableFiltersButton
          defaultFilters={{}}
          activeFilters={typedSearchParams}
          setFilters={setTypedSearchParams}
          ignoreKeys={['endDate']}
        />
      </div>
      {organizationList && (
        <div className="font-normal shrink-0 max-lg:w-full">
          <EntityPickerFilter
            selected={selectedOrganization || ''}
            onChange={value => handleFilterChangeWithStoreClear('selectedOrganization', value)}
            options={organizationList}
          />
        </div>
      )}
      {!!merchantAccounts?.length && (
        <div className="font-normal shrink-0 max-lg:w-full">
          <div className="font-normal shrink-0 max-lg:w-full">
            <MerchantAccountsPickerFilter
              selectedMerchantAccount={selectedMerchantAccount || ''}
              onSelectMerchantAccount={value =>
                handleFilterChangeWithStoreClear('selectedMerchantAccount', value)
              }
              merchantAccountsOptions={
                selectedOrganization
                  ? merchantAccounts.filter(
                      merchantAccount => merchantAccount.organizationId === selectedOrganization
                    )
                  : merchantAccounts
              }
              allLabel={i18n._('Merchant')}
            />
          </div>
        </div>
      )}
      {!!storeList?.length && (
        <div className="font-normal shrink-0 max-lg:w-full">
          <div className="font-normal shrink-0 max-lg:w-full">
            <EntityPickerFilter
              selected={storeId}
              label={i18n._('Store')}
              allLabel={i18n._('All Stores')}
              onChange={value => handleFilterChange('storeId', value)}
              options={storeList
                .sort((a, b) => (a.status === 'ACTIVE' ? -1 : b.status === 'ACTIVE' ? 1 : 0))
                .map(store => ({
                  name: store.name,
                  id: store.id,
                  ...(store.status !== 'ACTIVE' && {
                    className: 'text-gray-400',
                    classNameActive: 'text-gray-400',
                    tooltipText: 'Store is not active',
                  }),
                }))}
              className="w-full"
            />
          </div>
        </div>
      )}
      <div className="z-20 w-full mt-4 shrink-0 md:mt-0 lg:w-96">
        <DashboardDatepicker
          onChange={handleDateChange}
          dateRange={
            dateValue
              ? {
                  startDate: DateTime.fromJSDate(dateValue.startDate).toJSDate(),
                  endDate: DateTime.fromJSDate(dateValue.endDate).toJSDate(),
                }
              : null
          }
          showTimePicker
        />
      </div>
    </div>
  );
};
